import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import type { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import type {
  IDateFormFieldProps,
  ISelectFormFieldProps
} from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { trimTrailingZeros } from '@utils/formatting-utils';

import cloneDeep from 'lodash/cloneDeep';
import PrecisionService from 'src/core/services/PrecisionService';
import { CreateTaxationInput, IBaseDrawerProps, IIdCodeName } from '../types';
import {
  useCreateTaxation,
  useGetActiveLinePlans,
  useGetCurrencies,
  useGetLatestTaxationByLine
} from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { getTaxationDrawerInitialSections } from '@common/constants';
import {
  useGetTaxationPlanSpecific
} from '@features/configurations/taxation/taxation.slice';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

const toLineLookup = (activeLines: IIdCodeName[]) => {
  const result: Record<string, string> = {};
  const sorted = [...activeLines].sort(
    (a, b) => Number(a.code) - Number(b.code)
  );

  sorted.forEach((l) => {
    result[l.id] = `${l.code} - ${l.name}`;
  });

  return result;
};

const formatDateOnly = (date: Date): string => {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface Props extends IBaseDrawerProps {
  activeLines: IIdCodeName[];
}

const NewTaxationDrawer: React.FC<Props> = ({ onSuccess, activeLines }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>(
    getTaxationDrawerInitialSections()
  );

  const [forceDisable, setForceDisable] = useState(true);

  const { data: currenciesData } = useGetCurrencies.useQuery();

  const [getLatestByLine] = useGetLatestTaxationByLine.useLazy();
  const [getPlans] = useGetActiveLinePlans.useLazy();
  const [getPlanSpecific] = useGetTaxationPlanSpecific();

  const [createTaxationMutation] = useCreateTaxation();

  const currencyLookup = useMemo<Record<string, string>>(() => {
    const lookup: Record<string, string> = {};
    const list = currenciesData?.core?.getCurrencies ?? [];
    list.forEach((c: any) => {
      lookup[c.id] = c.title;
    });
    return lookup;
  }, [currenciesData]);

  const lineLookup = useMemo(() => toLineLookup(activeLines), [activeLines]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const next = cloneDeep(getTaxationDrawerInitialSections());
        const mainInputs = next[0].inputs as any;
        const repeater = next[1].inputs.customRepeater as any;

        (mainInputs.line as ISelectFormFieldProps).selectOptions = {
          ...lineLookup
        };
        (mainInputs.fixedStampCurrency as ISelectFormFieldProps).selectOptions =
          { ...currencyLookup };

        repeater.inputs.fixedStampCurrency.selectOptions = {
          ...currencyLookup
        };

        setForceDisable(false);
        setSections(next);
      } catch {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineLookup, currencyLookup]);

  const applyLatestTaxationForLine = async (lineId: string) => {
    const next = cloneDeep(sections);
    const mainInputs = next[0].inputs as any;
    const repeater = next[1].inputs.customRepeater as any;

    const latestRes = await getLatestByLine({ variables: { lineId } });
    const latest = latestRes?.data?.core?.getLatestTaxationByLine;

    mainInputs.line.value = [lineId];
    if (
      mainInputs.line.selectOptions &&
      !mainInputs.line.selectOptions[lineId]
    ) {
      mainInputs.line.selectOptions[lineId] = lineId;
    }

    if (latest) {
      mainInputs.fixedStamp.value = latest.fixedStamp?.toString() ?? '';
      mainInputs.fixedStampCurrency.value = latest.fixedStampCurrency
        ? [latest.fixedStampCurrency]
        : [];
      mainInputs.proportionalStamp.value = trimTrailingZeros(
        PrecisionService.multiplyBy100(Number(latest.proportionalStamp ?? 0))
      );
      mainInputs.municipalityTax.value = trimTrailingZeros(
        PrecisionService.multiplyBy100(Number(latest.municipalityTax ?? 0))
      );
      mainInputs.taxOnCommission.value = trimTrailingZeros(
        PrecisionService.multiplyBy100(Number(latest.taxOnCommission ?? 0))
      );
      mainInputs.reinsuranceTax.value = trimTrailingZeros(
        PrecisionService.multiplyBy100(Number(latest.reinsuranceTax ?? 0))
      );

      mainInputs.applicableToAllPlans.value =
        latest.applicableToAllPlans ?? true;

      if (latest.effectiveFrom) {
        const minDate = new Date(latest.effectiveFrom);
        minDate.setDate(minDate.getDate() + 1);
        (mainInputs.effectiveFrom as IDateFormFieldProps).minDate = minDate;
      }

      const hideRepeater = latest.applicableToAllPlans === true;
      next[1].hidden = hideRepeater;
      repeater.hidden = hideRepeater;

      if (!hideRepeater && latest.id) {
        const plansRes = await getPlans({ variables: { lineID: lineId } });
        const plansList = plansRes?.data?.planConfig?.getActiveLinePlans ?? [];

        const plansLookup: Record<string, string> = {};
        plansList.forEach((p: any) => {
          plansLookup[p.id] = `${p.code} - ${p.name}`;
        });
        repeater.inputs.plan.selectOptions = plansLookup;

        const rows: any[] = [];

        for (const p of plansList) {
          try {
            const res = await getPlanSpecific({
              variables: { lineId, planId: p.id, taxationId: latest.id },
              fetchPolicy: 'network-only'
            });

            const d = res?.data?.core?.getTaxationPlanSpecific;
            if (!d) continue;

            rows.push({
              plan: p.id,
              fixedStamp: d.fixedStamp?.toString() ?? '',
              fixedStampCurrency: d.fixedStampCurrency ?? '',
              proportionalStamp: trimTrailingZeros(
                PrecisionService.multiplyBy100(d.proportionalStamp ?? '')
              ),
              municipalityTax: trimTrailingZeros(
                PrecisionService.multiplyBy100(d.municipalityTax ?? '')
              ),
              reinsuranceTax: trimTrailingZeros(
                PrecisionService.multiplyBy100(d.reinsuranceTax ?? '')
              ),
              taxOnCommission: trimTrailingZeros(
                PrecisionService.multiplyBy100(d.taxOnCommission ?? '')
              )
            });
          } catch {
            // ignore row errors
          }
        }

        repeater.value = rows.length
          ? rows
          : [
              {
                plan: '',
                fixedStampCurrency: '',
                fixedStamp: '',
                proportionalStamp: '',
                municipalityTax: '',
                taxOnCommission: '',
                reinsuranceTax: ''
              }
            ];
      }
    }

    setSections(next);
  };

  const handleChange = async (
    fieldName: string,
    value: any,
    _sectionIndex: number,
    _values: any
  ) => {
    setForceDisable(true);

    if (fieldName === 'line' && value) {
      await applyLatestTaxationForLine(value);
      setForceDisable(false);
      return;
    }

    const next = cloneDeep(sections);
    const mainInputs = next[0].inputs as any;
    const repeater = next[1].inputs.customRepeater as any;

    if (fieldName === 'customRepeater') {
      repeater.value = value;
    } else {
      mainInputs[fieldName].value = value;
    }

    if (fieldName === 'applicableToAllPlans') {
      const hideRepeater = value === true;
      next[1].hidden = hideRepeater;
      repeater.hidden = hideRepeater;

      if (hideRepeater) {
        repeater.value = [];
      } else if (!repeater.value?.length) {
        repeater.value = [
          {
            plan: '',
            fixedStampCurrency: '',
            fixedStamp: '',
            proportionalStamp: '',
            municipalityTax: '',
            taxOnCommission: '',
            reinsuranceTax: ''
          }
        ];
      }
    }

    setSections(next);
    setForceDisable(false);
  };

  const submitForm = async (rawValues: Record<string, any>) => {
    try {
      const [data] = normalizeDynamicFormDrawerValues(sections, rawValues);

      let effectiveFromDate: string | null = null;
      if (data.effectiveFrom) {
        const dateObj =
          typeof data.effectiveFrom === 'string'
            ? new Date(data.effectiveFrom)
            : data.effectiveFrom;
        effectiveFromDate = formatDateOnly(dateObj);
      }

      if (!effectiveFromDate || effectiveFromDate === 'Invalid Date') {
        throw new Error('Please select a valid effective date.');
      }

      const taxationInput: CreateTaxationInput = {
        applicableToAllPlans: data.applicableToAllPlans,
        effectiveFrom: effectiveFromDate,
        fixedStamp: parseFloat(data.fixedStamp),
        fixedStampCurrency: Array.isArray(data.fixedStampCurrency)
          ? data.fixedStampCurrency[0]
          : data.fixedStampCurrency,
        lineID: Array.isArray(data.line) ? data.line[0] : data.line,
        municipalityTax: PrecisionService.divideBy100(data.municipalityTax),
        proportionalStamp: PrecisionService.divideBy100(data.proportionalStamp),
        reinsuranceTax: PrecisionService.divideBy100(data.reinsuranceTax),
        taxOnCommission: PrecisionService.divideBy100(data.taxOnCommission)
      };

      if (!data.applicableToAllPlans && Array.isArray(data.customRepeater)) {
        taxationInput.planSpecificTaxations = data.customRepeater.map(
          (item: any) => ({
            planID: item.plan,
            fixedStampCurrency: Array.isArray(item.fixedStampCurrency)
              ? item.fixedStampCurrency[0]
              : item.fixedStampCurrency,
            fixedStamp: parseFloat(item.fixedStamp),
            municipalityTax: PrecisionService.divideBy100(item.municipalityTax),
            proportionalStamp: PrecisionService.divideBy100(
              item.proportionalStamp
            ),
            reinsuranceTax: PrecisionService.divideBy100(item.reinsuranceTax),
            taxOnCommission: PrecisionService.divideBy100(item.taxOnCommission)
          })
        );
      }

      const res = await createTaxationMutation({
        taxationInput: taxationInput
      });

      const taxationId = res?.data?.core?.createTaxation;
      onSuccess(`/configuration/taxation/${taxationId}`);
    } catch (error) {
      console.error(error);
      toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      onChange={handleChange}
      forceDisable={forceDisable}
      submitButtonState={submitButtonState}
    />
  );
};

export { NewTaxationDrawer };
